import { Desktop, Mobile } from "@/lib/common/MediaQueries"
import { calculateTimeLeft } from "@/lib/utils"
import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import { useEffect, useState } from "react"

const TimeLeft = styled.div`
  display: flex;
  gap: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: fit-content;
    gap: 16px;
  }
`

const TimeLeftItem = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: fit-content;
    gap: 8px;
  }
  ${(props) => props.theme.breakpoints.down("md")} {
    h2 {
      font-size: 24px;
      line-height: 35px;
    }
  }
  * {
    letter-spacing: unset;
  }
`

const LabelledTimer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 24px;
`

const LabelledUnit = styled.div`
  display: flex;
  white-space: nowrap;
  flex-direction: column;
  align-items: center;
  min-width: 32px;
  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    min-width: unset;
    gap: 8px;
  }
`

const LabelledNumber = styled.span`
  font-size: 24px;
  font-weight: 400;
  line-height: normal;
  letter-spacing: unset;
  font-variant-numeric: tabular-nums;
  text-align: center;
  margin-bottom: -4px;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 40px;
    margin-bottom: 0;
  }
`

const LabelledLabel = styled.span`
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
  letter-spacing: unset;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
    min-width: 28px;
  }
`

const VerticalCenter = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`

export default function Timer({
  dueDate,
  fromYellowBanner,
  simple,
  large,
  withUnitLabels,
}: {
  dueDate: string
  fromYellowBanner?: boolean
  simple?: boolean
  large?: boolean
  withUnitLabels?: boolean
}): React.ReactElement {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(dueDate))
  const { hours, minutes, seconds } = timeLeft

  const array = [
    { time: hours, text: "שע'" },
    { time: minutes, text: "דק'" },
    { time: seconds, text: "שנ'" },
  ]

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft(dueDate))
    }, 1000)

    return () => clearInterval(timer)
  }, [dueDate])
  if (withUnitLabels) {
    const days = Math.floor(hours / 24)
    const pad = (value: number): string => String(value).padStart(2, "0")
    type Unit = { value: string; label: string; valueWidth?: string }
    const ticking = (amount: number, label: string): Unit => ({
      value: pad(amount),
      label,
      valueWidth: "2ch",
    })
    const units: Unit[] =
      days > 0
        ? [
            { value: String(days), label: "ימ׳" },
            ticking(hours % 24, "שע׳"),
            ticking(minutes, "דק׳"),
          ]
        : [ticking(hours, "שע׳"), ticking(minutes, "דק׳"), ticking(seconds, "שנ׳")]
    return (
      <LabelledTimer>
        {units.map((unit) => (
          <LabelledUnit key={unit.label}>
            <LabelledNumber suppressHydrationWarning style={{ minWidth: unit.valueWidth }}>
              {unit.value}
            </LabelledNumber>
            <LabelledLabel>{unit.label}</LabelledLabel>
          </LabelledUnit>
        ))}
      </LabelledTimer>
    )
  }
  if (hours >= 41) {
    const days = Math.floor(hours / 24)
    return (
      <TimeLeft>
        <TimeLeftItem>
          <Desktop>
            <VerticalCenter>
              {!simple && !fromYellowBanner && (
                <Typography variant="body1">ההצעה נגמרת בעוד</Typography>
              )}
              <Typography variant="h2" fontWeight={400} fontSize={32} lineHeight={"100%"}>
                {days}
              </Typography>
              <Typography variant="body1" fontWeight={400} fontSize={32} lineHeight={"100%"}>
                ימים
              </Typography>
            </VerticalCenter>
          </Desktop>
          <Mobile>
            <VerticalCenter>
              {!simple && !fromYellowBanner && (
                <Typography variant="body1">ההצעה נגמרת בעוד</Typography>
              )}
              <Typography variant="h3"> {days} </Typography>
              <Typography variant="body1">ימים</Typography>
            </VerticalCenter>
          </Mobile>
        </TimeLeftItem>
      </TimeLeft>
    )
  }
  if (simple) {
    const timeString = array?.map((item) => item.time)?.join(":")
    return (
      <TimeLeft>
        <TimeLeftItem>
          <Typography variant={large ? "h2" : "h3"}>{timeString}</Typography>
        </TimeLeftItem>
      </TimeLeft>
    )
  }
  return (
    <TimeLeft>
      {array.map((item) => (
        <TimeLeftItem key={item.text}>
          <Desktop>
            <Typography variant="h2" fontWeight={600}>
              {item.time}
            </Typography>
          </Desktop>
          <Desktop>
            <Typography variant="body1">{item.text}</Typography>
          </Desktop>
          <Mobile>
            <Typography variant="h3">{item.time}</Typography>
          </Mobile>
          <Mobile>
            <Typography variant="body1">{item.text}</Typography>
          </Mobile>
        </TimeLeftItem>
      ))}
    </TimeLeft>
  )
}
